import { reactive, watch } from 'vue';
import api from '/src/api.js';
import { deepClone, deepEqual } from '/src/utils/helpers.js';

export function useForm (data) {
	let defaults = deepClone(data);
	let transform = data => data;

	const form = reactive({
		...defaults,
		processing: false,
		isDirty: false,
		errors: {},
		hasErrors: false,
		data () {
			return Object.keys(defaults).reduce((carry, key) => {
				carry[key] = this[key];
				return carry;
			}, {});
		},
		transform (callback) {
			transform = callback;

			return this;
		},
		defaults (fields) {
			if (typeof fields === 'undefined') {
				defaults = this.data();
			} else {
				defaults = Object.assign(
					{},
					deepClone(defaults),
					fields,
				);
			}

			return this;
		},
		reset (...fields) {
			const resolvedData = deepClone(defaults);
			const clonedData = deepClone(resolvedData);

			if (fields.length === 0) {
				defaults = clonedData;
				Object.assign(this, resolvedData);
			} else {
				Object.keys(resolvedData)
					.filter(key => fields.includes(key))
					.forEach(key => {
						defaults[key] = clonedData[key];
						this[key] = resolvedData[key];
					})
				;
			}

			return this;
		},
		setErrors (fields) {
			Object.assign(
				this.errors,
				fields,
			);

			this.hasErrors = Object.keys(this.errors).length > 0;

			return this;
		},
		clearErrors (...fields) {
			this.errors = Object.keys(this.errors).reduce(
				(carry, field) => ({
					...carry,
					...(fields.length > 0 && !fields.includes(field)
						? { [field]: this.errors[field] }
						: {}),
				}),
				{},
			);

			this.hasErrors = Object.keys(this.errors).length > 0;

			return this;
		},
		submit (method, url, options = {}) {
			const data = transform(this.data());
			const config = {
				...options,
				url,
				data,
				onStart: () => {
					this.processing = true;

					if (options.onStart) {
						return options.onStart();
					}
				},
				onSuccess: (data) => {
					this.clearErrors();
					this.isDirty = false;

					if (options.onSuccess) {
						return options.onSuccess(data);
					}
				},
				onError: (errors, raw) => {
					this.clearErrors().setErrors(errors);

					if (options.onError) {
						return options.onError(errors, raw);
					}
				},
				onFinish: () => {
					this.processing = false;

					if (options.onFinish) {
						return options.onFinish();
					}
				},
			};

			return api.submit(method, url, config);
		},
		get (url, options) {
			return this.submit('get', url, options);
		},
		post (url, options) {
			return this.submit('post', url, options);
		},
		put (url, options) {
			return this.submit('put', url, options);
		},
		patch (url, options) {
			return this.submit('patch', url, options);
		},
		delete (url, options) {
			return this.submit('delete', url, options);
		},
		cancel () {
			api.cancel();
		},
	});

	watch(
		form,
		() => {
			form.isDirty = !deepEqual(form.data(), defaults);
		},
		{ immediate: true, deep: true },
	);

	return form;
}
